import { useCallback, useMemo } from 'react';
import { useAuth } from 'react-oidc-context';
import { z } from 'zod';
import { checkRuntimeType } from '@/app/utils/checkRunTime.ts';

// as this cloudIDP account is shares with CP and has new roles, they must be added here as faster solution to avoid
// SF portal will fail because enum comparisons throw an error when some value is not present in the set, despite it is not used

export enum GROUPS {
  VWAG_CP_COMPLIANCE_OFFICER = 'VWAG_CP_COMPLIANCE_OFFICER',
  VWAG_CP_MANAGER = 'VWAG_CP_MANAGER'
}

const GroupsSchema = z.preprocess(
  (val) =>
    Array.isArray(val)
      ? val.filter((v) => Object.values(GROUPS).includes(v))
      : [],
  z.array(z.enum(GROUPS))
);

export type Groups = z.infer<typeof GroupsSchema>;

export interface UseAuthorizationResult {
  checkAccess: (args: { allowedGroups: Groups }) => boolean;
  groups: Groups;
}

/**
 * Hook that provides authorization functionality.
 * @returns {UseAuthorizationResult} Object containing the user's groups and a function to check if the user belongs to one of the groups in a list of allowed groups.
 */
export const useAuthorization = (): UseAuthorizationResult => {
  const { user, isAuthenticated } = useAuth();

  const groups: Groups = useMemo(() => {
    if (!user?.profile?.groups) return [];
    // ['VWAG_CP_MANAGER']
    const creatior_group = (user?.profile?.groups as string[])?.filter(
      (role) => role === 'VWAG_CP_COMPLIANCE_OFFICER'
    );
    try {
      return checkRuntimeType(
        GroupsSchema,
        creatior_group?.length ? creatior_group : user?.profile?.groups
      ) as Groups;
    } catch (e) {
      console.error('Error validating user groups runtime type:', e);
      return [];
    }
  }, [user]);

  /**
   * Checks if the current users belongs to at least one of the allowed groups.
   * @param allowedGroups List of allowed groups
   * @returns {boolean} Returns true if the user belongs to at least one of the allowed groups, otherwise false.
   */
  const checkAccess = useCallback(
    ({ allowedGroups }: { allowedGroups: Groups }) => {
      if (!isAuthenticated || !user?.profile?.groups) return false;

      if (allowedGroups.length > 0) {
        return groups.some((group) => allowedGroups.includes(group));
      }

      return true;
    },
    [groups, isAuthenticated, user]
  );

  return { checkAccess, groups: groups };
};
